import { useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { fileListQuery } from '@xdocs/schemas';
import type { ErrorAlert, ExplorerFolderEntry, FileListResult } from '@xdocs/schemas';
import { ChevronRight, Folder as FolderIcon } from 'lucide-react';
import { Button, EmptyState, Modal, Skeleton, cn } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';
import { useExplorerT } from './useExplorerT.js';
import styles from './MoveDialog.module.css';

export interface MoveTarget {
  type: 'file' | 'folder';
  id: string;
  name: string;
  /** The entry's current parent — used only to short-circuit a no-op move. */
  currentFolderId: string | null;
}

export interface MoveDialogProps {
  target: MoveTarget | null;
  onClose: () => void;
  onMoveFile: (fileId: string, folderId: string | null) => Promise<void>;
  onMoveFolder: (folderId: string, parentFolderId: string | null) => Promise<void>;
}

/**
 * F7 "Move" picker — browses folders only via `file.list`'s server-side
 * `entryType: 'folder'` filter (§5.9 — never a fetch-all-then-filter over a
 * fixed page; a folder full of files, or a folder with >100 mixed children,
 * would otherwise push the target folder past the page and make it silently
 * unreachable). Only the trivial self-move is blocked here (`folderMoveInput`'s
 * own doc comment: the full ancestor-chain check needs the DB tree and is
 * server-side `CONFLICT`, not zod's job) — a deeper invalid move surfaces
 * through `applyWsAlert`'s banner.
 */
export function MoveDialog({ target, onClose, onMoveFile, onMoveFolder }: MoveDialogProps): ReactElement | null {
  const { t } = useExplorerT();
  const { wsClient, connectionState } = useWs();
  const [browsingFolderId, setBrowsingFolderId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery<FileListResult, ErrorAlert>({
    queryKey: ['move-picker', browsingFolderId],
    queryFn: () =>
      requestTyped(
        wsClient,
        'file.list',
        fileListQuery.parse({
          folderId: browsingFolderId,
          entryType: 'folder',
          page: 1,
          pageSize: 100,
          sort: 'name',
          dir: 'asc',
        }),
      ),
    enabled: target !== null && connectionState === 'connected',
  });

  if (!target) return null;

  // The server already scopes rows to folders (`entryType: 'folder'`) — this
  // only narrows the TS union for the JSX below and excludes the move
  // target itself (can't move a folder into itself).
  const folders = (query.data?.rows ?? []).filter(
    (row): row is ExplorerFolderEntry => row.type === 'folder' && row.id !== target.id,
  );

  async function handleMoveHere(): Promise<void> {
    if (!target) return;
    if (browsingFolderId === target.currentFolderId) {
      onClose();
      return;
    }
    setIsMoving(true);
    setError(null);
    try {
      if (target.type === 'folder') await onMoveFolder(target.id, browsingFolderId);
      else await onMoveFile(target.id, browsingFolderId);
      onClose();
    } catch (err) {
      const alert = err as { message?: string };
      setError(alert.message ?? 'errors:code.INTERNAL');
    } finally {
      setIsMoving(false);
    }
  }

  return (
    <Modal
      open
      title={t('explorer:move.title', { name: target.name })}
      description={t('explorer:move.description')}
      onClose={onClose}
      size="sm"
      footer={
        <div className={cn(styles['footer'])}>
          <Button variant="secondary" onClick={onClose} disabled={isMoving}>
            {t('explorer:action.cancel')}
          </Button>
          <Button variant="primary" state={isMoving ? 'loading' : 'idle'} onClick={() => void handleMoveHere()}>
            {t('explorer:move.confirm')}
          </Button>
        </div>
      }
    >
      <button type="button" className={cn(styles['crumb'])} onClick={() => setBrowsingFolderId(null)}>
        {t('explorer:move.root')}
      </button>

      {error && (
        <p role="alert" className={cn(styles['error'])}>
          {error}
        </p>
      )}

      {query.isLoading && <Skeleton shape="row" count={4} />}

      {query.isError && (
        <EmptyState kind="error" title={t('explorer:error.title')} action={{ label: t('explorer:action.cancel'), onClick: onClose }} />
      )}

      {!query.isLoading && !query.isError && folders.length === 0 && <p className={cn(styles['empty'])}>{t('explorer:move.empty')}</p>}

      {!query.isLoading && !query.isError && folders.length > 0 && (
        <ul className={cn(styles['list'])}>
          {folders.map((folder) => (
            <li key={folder.id}>
              <button type="button" className={cn(styles['row'])} onClick={() => setBrowsingFolderId(folder.id)}>
                <FolderIcon size={16} aria-hidden="true" />
                <span className={cn(styles['rowName'])}>{folder.name}</span>
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
